<template>
  <NuxtLink
    :to="articleUrl"
    class="article-main-card"
    itemscope
    itemtype="https://schema.org/Article"
  >
    <div class="image-wrapper">
      <img v-if="image" :src="image" :alt="name" loading="lazy" itemprop="image" />
    </div>

    <div class="date">
      <BaseIconSvoi icon="time/calendar" size="s" />
      <span class="date-text">{{ formattedDate }}</span>
    </div>

    <div class="content">
      <h3 class="title" itemprop="headline">{{ name }}</h3>
      <div class="arrow-icon">
        <BaseIconSvoi icon="arrow/right-up" size="m" />
      </div>
    </div>
  </NuxtLink>
</template>

<script lang="ts" setup>
  import { BaseIconSvoi } from '~/components/base-ui/icon'
  import { toMonthYearDateFormat } from '~/shared/utils/date'

  const props = defineProps<{
    slug: string
    name: string
    image: string
    publishedAt: string
    link?: string
  }>()

  const articleUrl = computed(() => props.link || `/news/${props.slug}`)
  const formattedDate = computed(() =>
    props.publishedAt ? toMonthYearDateFormat(props.publishedAt) : ''
  )
</script>

<style lang="scss" scoped>
  .article-main-card {
    display: block;
    border-radius: 16px;
    overflow: hidden;
    text-decoration: none;
    position: relative;
    width: 100%;

    &:hover {
      > .content > .arrow-icon {
        color: $color-kiwi-60;
      }

      > .image-wrapper > img {
        transform: scale(1.1);
      }
    }

    > .image-wrapper {
      position: relative;
      width: 100%;
      height: 204px;
      overflow: hidden;
      border-radius: 16px;

      @include vp-767 {
        height: 220px;
      }

      > img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        transition: all 0.6s ease;
      }
    }

    > .date {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 8px;

      @include vp-767 {
        padding: 6px;
        gap: 6px;
      }

      > .date-text {
        color: $color-potato-60;
        font-weight: 600;
        font-size: 14px;
        line-height: 22px;
        white-space: nowrap;

        @include vp-767 {
          font-size: 12px;
        }
      }
    }

    > .content {
      position: relative;
      padding: 0 8px 8px;
      display: flex;
      align-items: stretch;
      gap: 8px;

      @include vp-767 {
        padding: 0 6px 6px;
        gap: 6px;
      }

      > .title {
        font-weight: 400;
        font-size: 18px;
        line-height: 1.44;
        color: $color-default-black;
        margin: 0;
        flex: 1;
        display: -webkit-box;
        -webkit-line-clamp: 3;
        -webkit-box-orient: vertical;
        overflow: hidden;
        text-overflow: ellipsis;

        @include vp-767 {
          font-size: 16px;
          -webkit-line-clamp: 2;
        }
      }

      > .arrow-icon {
        flex-shrink: 0;
        width: 24px;
        height: 24px;
        display: flex;
        align-items: center;
        justify-content: center;
        color: $color-kiwi-50;
        transition: color 0.2s ease;
      }
    }
  }
</style>
